import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ErrorIllustration } from './ErrorIllustration';
import RefreshIcon from '@mui/icons-material/Refresh';

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void | Promise<unknown>;
  title?: string;
  compact?: boolean;
  loading?: boolean;
}

/** Consistent error panel. Technical error details belong in diagnostics, not this message. */
export function ErrorState({
  onRetry,
  title = "We couldn't load the data",
  compact = false,
  loading = false,
}: ErrorStateProps) {
  const [retrying, setRetrying] = useState(false);
  const retryInFlight = useRef(false);
  const busy = loading || retrying;

  const retry = async () => {
    if (!onRetry || busy || retryInFlight.current) return;
    retryInFlight.current = true;
    setRetrying(true);
    try {
      await onRetry();
    } catch {
      // The caller retains its error state; another attempt remains available.
    } finally {
      retryInFlight.current = false;
      setRetrying(false);
    }
  };

  return (
    <Box
      aria-busy={busy}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1.5,
        px: 3,
        py: compact ? 3 : 6,
        width: '100%',
      }}
    >
      <Box role="alert" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <ErrorIllustration compact={compact} />
        <Typography variant="h4" component="p">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
          Please check your connection and try again.
        </Typography>
      </Box>
      {onRetry ? (
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => void retry()}
          loading={busy}
          sx={{ mt: 1 }}
        >
          Try again
        </Button>
      ) : null}
      {busy ? <Typography role="status" variant="body2" color="text.secondary">Retrying…</Typography> : null}
    </Box>
  );
}
